import React, { useEffect, useId, useRef, useState } from 'react';
import { cx } from '../cx';
import { Tag } from './Tag';
import { Checkbox } from './Checkbox';
import type { ComboboxOption } from './Combobox';

// D-09 §15.47 — 다중 선택 드롭다운 (MultiSelect)
// 단일형 Combobox(§15.36)와 트리거·목록 규격을 공유한다. 다른 점은 셋뿐이다.
//  1) 선택값을 트리거 안에 칩(§15.5 Tag)으로 쌓는다 — 폭 대신 높이가 늘어난다.
//  2) 목록은 `.menu-multi` — 항목마다 체크박스가 서고, 골라도 닫히지 않는다(연속 선택).
//  3) 값이 배열이다. 비동기 loadOptions 는 갖지 않는다(정적 options 전용, §15.36 단일형만 지원).
// 선택 옵션(기본 꺼짐 — 기존 사용처 동작 그대로): renderOption(항목 배지) · caretToggle(▾/▴ 토글) ·
// enterSelectsSingle(검색 결과 1건 Enter 추가). 현장 담당자·관리자 선택(2026-10-12 개선사항 D.0)에서 쓴다.
// 칩 옵션(§15.52): toggled 를 넘기면 칩 라벨 뒤에 켬/끔 토글 아이콘이 붙는다(사람별 알림 받기 등 — 새로 고른 값은 꺼진 채로,
// 칩을 지우면 토글 목록에서도 빠진다). removable={false} 면 칩 × 를 숨긴다(해제는 목록 체크로만).
export interface MultiSelectProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: ComboboxOption[];
  /** true 면 트리거 안에서 타이핑해 후보를 거른다 */
  searchable?: boolean;
  label?: string;
  error?: string;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** 목록 항목 표시를 바꾼다(예: 오른쪽 배지). 미지정이면 label 그대로. 칩은 항상 label */
  renderOption?: (opt: ComboboxOption, state: { selected: boolean }) => React.ReactNode;
  /** true 면 우측 화살표(▾/▴)가 열기/닫기 토글 버튼이 된다 */
  caretToggle?: boolean;
  /** true 면 검색 결과가 1건일 때 Enter 로 바로 선택(이미 선택돼 있으면 그대로)하고 검색어를 비운다 */
  enterSelectsSingle?: boolean;
  /** 칩 토글이 켜진 값 목록(values 의 부분집합). 넘기면 칩마다 토글 아이콘이 붙는다 */
  toggled?: string[];
  onToggledChange?: (toggled: string[]) => void;
  /** 토글의 의미 — 버튼 aria-label·툴팁에 쓴다 */
  toggleLabel?: string;
  /** 켜짐/꺼짐 아이콘 — 기본은 종 / 사선 종 */
  iconOn?: React.ReactNode;
  iconOff?: React.ReactNode;
  /** false 면 칩 × 와 빈 검색어 Backspace 삭제를 끈다 */
  removable?: boolean;
}

const svg = {
  width: 14,
  height: 14,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

const BellOn = (
  <svg {...svg}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const BellOff = (
  <svg {...svg}>
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
    <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
    <path d="M18 8a6 6 0 0 0-9.33-5" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export function MultiSelect({
  values,
  onChange,
  options,
  searchable = false,
  label,
  error,
  placeholder = '선택하세요',
  emptyText = '항목이 없습니다',
  disabled,
  className = '',
  id,
  renderOption,
  caretToggle = false,
  enterSelectsSingle = false,
  toggled,
  onToggledChange,
  toggleLabel = '알림 받기',
  iconOn = BellOn,
  iconOff = BellOff,
  removable = true,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const items = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  // 옵션 목록에 없는 값도 칩은 남긴다 — 라벨을 못 찾았다고 선택이 사라져 보이면 안 된다.
  const selected = values.map((v) => options.find((o) => o.value === v) ?? { value: v, label: v });

  const openMenu = () => {
    if (disabled || open) return;
    setOpen(true);
    setActiveIndex(-1);
    if (searchable) requestAnimationFrame(() => searchRef.current?.focus());
  };

  const closeMenu = () => {
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
  };

  // 선택에서 빠진 값은 토글 목록에서도 뺀다 — 다시 고르면 꺼진 채로 들어온다.
  const remove = (value: string) => {
    onChange(values.filter((v) => v !== value));
    if (toggled?.includes(value)) onToggledChange?.(toggled.filter((v) => v !== value));
  };

  const toggle = (opt: ComboboxOption) => {
    if (opt.disabled) return; // 비활성 옵션은 선택 불가
    if (values.includes(opt.value)) remove(opt.value);
    else onChange([...values, opt.value]);
    // 선택 후 검색어 초기화(연속 선택 편의) — 포커스는 유지
    if (searchable) {
      setQuery('');
      setActiveIndex(-1);
    }
  };

  const flip = (value: string) =>
    onToggledChange?.(toggled?.includes(value) ? toggled.filter((v) => v !== value) : [...(toggled ?? []), value]);

  // 바깥 클릭 닫기
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) closeMenu();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) return openMenu();
      setActiveIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === ' ') {
      if (!open) {
        e.preventDefault();
        return openMenu();
      }
      if (activeIndex >= 0 && items[activeIndex]) {
        e.preventDefault();
        toggle(items[activeIndex]);
      } else if (enterSelectsSingle && e.key === 'Enter' && query && items.length === 1) {
        // 검색 결과 1건 — 해제 토글이 아니라 추가만 한다(이미 있으면 검색어만 비움)
        e.preventDefault();
        const only = items[0];
        if (!only.disabled && !values.includes(only.value)) onChange([...values, only.value]);
        setQuery('');
        setActiveIndex(-1);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.stopPropagation();
        closeMenu();
      }
    } else if (e.key === 'Backspace' && removable && searchable && !query && values.length) {
      // 검색어가 빈 상태의 Backspace — 마지막 칩을 지운다(태그 입력 관례)
      remove(values[values.length - 1]);
    }
  };

  const renderToggle = (value: string) => {
    const on = toggled!.includes(value);
    return (
      <button
        type="button"
        className={cx('tag-toggle', on && 'is-on')}
        aria-pressed={on}
        aria-label={toggleLabel}
        title={on ? `${toggleLabel} · 켜짐` : `${toggleLabel} · 꺼짐`}
        disabled={disabled}
        onClick={() => flip(value)}
        // 칩 안 버튼의 Enter/Space 가 트리거의 메뉴 열기 키로 번지지 않게 한다
        onKeyDown={(e) => e.stopPropagation()}
      >
        {on ? iconOn : iconOff}
      </button>
    );
  };

  return (
    <div className={cx('field', 'field-auto', error && 'error', className)}>
      {label && <label htmlFor={id}>{label}</label>}
      <div className={cx('combo', 'combo-multi', toggled && 'combo-multi-toggle', open && 'open')} ref={rootRef}>
        <div
          id={id}
          className={cx('input', disabled && 'is-disabled')}
          role="combobox"
          tabIndex={disabled ? -1 : 0}
          aria-expanded={open}
          aria-controls={listId}
          aria-disabled={disabled || undefined}
          onClick={(e) => {
            // 칩의 토글·제거 버튼(§15.5 Tag > button) 클릭은 메뉴를 열지 않는다
            if ((e.target as HTMLElement).closest('button')) return;
            openMenu();
          }}
          onKeyDown={onKeyDown}
        >
          {selected.map((opt) => (
            <Tag
              key={opt.value}
              onRemove={disabled || !removable ? undefined : () => remove(opt.value)}
            >
              {opt.label}
              {toggled && renderToggle(opt.value)}
            </Tag>
          ))}
          {searchable && open ? (
            <input
              ref={searchRef}
              className="ms-search"
              value={query}
              aria-label={label ?? placeholder}
              placeholder={selected.length ? '' : placeholder}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(-1);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            selected.length === 0 && <span className="ms-placeholder">{placeholder}</span>
          )}
        </div>
        {caretToggle ? (
          <button
            type="button"
            className="caret"
            aria-label={open ? '목록 닫기' : '목록 열기'}
            tabIndex={-1}
            disabled={disabled}
            style={{ pointerEvents: 'auto', background: 'none', border: 'none', padding: 0, cursor: disabled ? 'not-allowed' : 'pointer' }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => (open ? closeMenu() : openMenu())}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : (
          <span className="caret" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
        {open && (
          <div className="menu menu-multi show" role="listbox" aria-multiselectable="true" id={listId}>
            {items.length === 0 && <div className="combo-empty">{emptyText}</div>}
            {items.map((opt, i) => (
              <div
                key={opt.value}
                role="option"
                aria-selected={values.includes(opt.value)}
                aria-disabled={opt.disabled || undefined}
                className={cx('menu-item', values.includes(opt.value) && 'selected', i === activeIndex && !opt.disabled && 'active')}
                style={opt.disabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => toggle(opt)}
                onMouseEnter={() => { if (!opt.disabled) setActiveIndex(i); }}
              >
                <Checkbox bare checked={values.includes(opt.value)} readOnly disabled={opt.disabled} tabIndex={-1} />
                {renderOption ? renderOption(opt, { selected: values.includes(opt.value) }) : opt.label}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && <span className="help">{error}</span>}
    </div>
  );
}
